import { useEffect, type RefObject } from "react";

/**
 * useDismiss closes a popover: a tap outside it, or Escape.
 *
 * Every menu, badge and armed button in the app had its own copy of this, and
 * they had already drifted — one listened for Escape and one did not, so the
 * keyboard closed the account menu and not the delete confirmation. Which is
 * exactly the kind of difference nobody notices and nobody reports.
 *
 * `pointerdown`, not `click`. A click fires after the pointer is released, so a
 * press that starts outside the popover and drags into it counts as a click on
 * the popover — and a menu dismissed on `click` stays open under a thumb that
 * has already moved on. `pointerdown` also beats the browser's own focus
 * handling to the decision, which is what a menu wants.
 *
 * Listening only while open is not an optimisation: a closed popover that keeps
 * a document-level handler is a handler that runs on every tap in the app, once
 * per closed popover on the page.
 */
export function useDismiss(
  open: boolean,
  /** The elements the popover lives inside; a tap in any is not "outside".
   *  More than one when part of it is portalled out of its trigger's tree. */
  within: RefObject<HTMLElement | null> | RefObject<HTMLElement | null>[],
  onDismiss: () => void,
  opts: { escape?: boolean } = {},
) {
  const { escape = true } = opts;
  useEffect(() => {
    if (!open) return;
    const refs = Array.isArray(within) ? within : [within];
    const away = (e: PointerEvent) => {
      if (!refs.some((r) => r.current?.contains(e.target as Node))) onDismiss();
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") onDismiss();
    };
    document.addEventListener("pointerdown", away);
    if (escape) document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("pointerdown", away);
      if (escape) document.removeEventListener("keydown", key);
    };
    // onDismiss is expected to be stable or cheap to re-subscribe on; every
    // caller passes a setState or a prop, both of which are.
  }, [open, within, onDismiss, escape]);
}
